import { Component, FC, ReactNode } from 'react';
import { GoogleLogin, useGoogleOAuth } from '@react-oauth/google';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGoogleSignInMutation } from '../../services/auth';
import * as routes from '../../pages/routes';
import TokenManager from '../../utils/tokenManager';
import { returnPathFrom } from '@/utils/loginReturn';
import useFinishSignIn from '@/hooks/useFinishSignIn';
import snackbar from '@/utils/snackbar';

const GOOGLE_SIGN_IN_FAILED = 'ورود با گوگل ناموفق بود. لطفاً دوباره تلاش کنید.';

/**
 * Error boundary scoped to just the Google sign-in button.
 *
 * Google Identity Services is served from accounts.google.com — a host that
 * is frequently slow or outright blocked on the networks our users are on.
 * If anything inside the GSI-backed <GoogleLogin> throws at runtime (script
 * only partially loaded, an error thrown from window.google.accounts, etc.)
 * this boundary swallows it and renders nothing. A Google failure can never
 * again take down the whole login/register page — the bug we hit before.
 */
class GoogleButtonBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    // eslint-disable-next-line no-console
    console.warn('[auth] Google sign-in button crashed; hiding it.', error);
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

const LoginWithGoogle: FC<{
  text: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
}> = ({ text }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = returnPathFrom(location.state);
  const finishSignIn = useFinishSignIn();
  const [googleSignIn, { isLoading }] = useGoogleSignInMutation();
  // `scriptLoadedSuccessfully` is false while GSI is loading AND stays false
  // if it never loads (offline / blocked). Provided by GoogleOAuthProvider,
  // which is mounted at the app root, so this hook never throws here.
  const { scriptLoadedSuccessfully } = useGoogleOAuth();

  // GSI isn't available — render nothing rather than an empty GSI slot.
  // The rest of the login/register form is completely unaffected.
  if (!scriptLoadedSuccessfully) return null;

  return (
    <GoogleButtonBoundary>
      {/* Dimmed and unclickable while the sign-in request runs. */}
      <div
        style={{
          opacity: isLoading ? 0.5 : 1,
          pointerEvents: isLoading ? 'none' : 'auto'
        }}>
      <GoogleLogin
        locale="fa_IR"
        type="icon"
        text={text}
        onSuccess={(credentialResponse) => {
          // One sign-in at a time: a second tap would race the first on the stored token.
          if (isLoading) return;
          googleSignIn({ idToken: credentialResponse.credential || '' })
            .unwrap()
            .then((res) => {
              if (res.data.isRegisterNeeded) {
                TokenManager.set(res.data).then(() => {
                  navigate(routes.GOOGLE_REGISTER, { state: { returnTo } });
                });
              } else {
                finishSignIn(res.data, 'login', returnTo);
              }
            })
            .catch((error) => {
              // A server message is already toasted by rtkQueryErrorLogger; a failure without one
              // (network) would otherwise pass silently.
              if (!error?.data?.message) snackbar.error(GOOGLE_SIGN_IN_FAILED);
            });
        }}
        onError={() => snackbar.error(GOOGLE_SIGN_IN_FAILED)}
      />
      </div>
    </GoogleButtonBoundary>
  );
};

export default LoginWithGoogle;
